import { useState, useEffect } from "react";
import { ChevronDown, ChevronRight } from "lucide-react";
import { QuestingGroupChipSelector } from "./QuestingGroupChipSelector.jsx";

const FIT_MODES = [
  { value: "start-only", label: "Start only", hint: "Slot start must fall inside the window" },
  { value: "full-duration", label: "Full duration", hint: "Slot start AND end must fit" },
  { value: "with-leeway", label: "With leeway", hint: "Allow leeway minutes on both sides" },
];

const DEFAULT_VALUE = {
  leewayFrontMinutes: 0,
  leewayBackMinutes: 0,
  fitMode: "full-duration",
  appliesToQuestingGroupIds: null,
};

function valuesDivergeFromDefaults(value, defaults) {
  if (!value || !defaults) return false;
  if ((value.leewayFrontMinutes ?? 0) !== (defaults.leewayFrontMinutes ?? 0)) return true;
  if ((value.leewayBackMinutes ?? 0) !== (defaults.leewayBackMinutes ?? 0)) return true;
  if ((value.fitMode ?? DEFAULT_VALUE.fitMode) !== (defaults.fitMode ?? DEFAULT_VALUE.fitMode)) return true;
  // Group scoping diverges from "global" (null) only if the array is non-empty.
  const ids = value.appliesToQuestingGroupIds;
  if (Array.isArray(ids) && ids.length > 0) return true;
  return false;
}

/**
 * Phase 3c-2 U1: collapsible "Advanced" section housing leeway / fit-mode /
 * group-scoping fields. Reused by MarkEditorDialog (U3) and LinkCalendarDialog
 * (U4 — free-type only). Closed by default; force-opens when current values
 * diverge from defaults so editing a mark with non-default settings shows
 * them at a glance.
 */
export function AdvancedFieldsSection({
  value = DEFAULT_VALUE,
  defaults = DEFAULT_VALUE,
  onChange,
  forceOpen = false,
}) {
  const shouldForceOpen = forceOpen || valuesDivergeFromDefaults(value, defaults);
  const [isOpen, setIsOpen] = useState(shouldForceOpen);

  // Re-evaluate force-open when the value transitions from defaults to
  // non-defaults across renders (e.g. switching from create to edit).
  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect -- intentional: synchronous state init/reset for this subscription effect
    if (shouldForceOpen && !isOpen) setIsOpen(true);
    // intentionally don't auto-close when divergence drops to zero; user can
    // close manually after they've reviewed.
  }, [shouldForceOpen, isOpen]);

  const patch = (changes) => onChange?.({ ...value, ...changes });

  const leewayFront = Number(value.leewayFrontMinutes ?? defaults.leewayFrontMinutes ?? 0);
  const leewayBack = Number(value.leewayBackMinutes ?? defaults.leewayBackMinutes ?? 0);
  const fitMode = value.fitMode ?? defaults.fitMode ?? DEFAULT_VALUE.fitMode;
  const groupIds = Array.isArray(value.appliesToQuestingGroupIds)
    ? value.appliesToQuestingGroupIds
    : null;

  return (
    <div className="border-t border-slate-200 dark:border-slate-700 pt-3 mt-3">
      <button
        type="button"
        onClick={() => setIsOpen((prev) => !prev)}
        className="flex items-center gap-2 text-sm font-medium text-slate-700 dark:text-slate-300 hover:text-slate-900 dark:hover:text-slate-100"
        aria-expanded={isOpen}
        aria-controls="advanced-fields-content"
      >
        {isOpen ? (
          <ChevronDown className="h-4 w-4" aria-hidden="true" />
        ) : (
          <ChevronRight className="h-4 w-4" aria-hidden="true" />
        )}
        <span>Advanced</span>
        {!isOpen && shouldForceOpen ? (
          <span className="text-xs text-amber-600 dark:text-amber-400">
            (custom settings active)
          </span>
        ) : null}
      </button>

      {isOpen ? (
        <div id="advanced-fields-content" className="mt-3 space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <label className="block text-sm">
              <span className="text-slate-700 dark:text-slate-300">
                Leeway front (minutes)
              </span>
              <input
                type="number"
                min={0}
                max={120}
                step={5}
                value={leewayFront}
                onChange={(e) =>
                  patch({ leewayFrontMinutes: Number(e.target.value) || 0 })
                }
                className="mt-1 block w-full rounded-md border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 px-2 py-1 text-sm text-slate-900 dark:text-slate-100"
              />
            </label>
            <label className="block text-sm">
              <span className="text-slate-700 dark:text-slate-300">
                Leeway back (minutes)
              </span>
              <input
                type="number"
                min={0}
                max={120}
                step={5}
                value={leewayBack}
                onChange={(e) =>
                  patch({ leewayBackMinutes: Number(e.target.value) || 0 })
                }
                className="mt-1 block w-full rounded-md border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 px-2 py-1 text-sm text-slate-900 dark:text-slate-100"
              />
            </label>
          </div>

          <fieldset>
            <legend className="text-sm text-slate-700 dark:text-slate-300 mb-1">
              Fit mode
            </legend>
            <div className="space-y-1">
              {FIT_MODES.map((mode) => (
                <label
                  key={mode.value}
                  className="flex items-start gap-2 text-sm"
                >
                  <input
                    type="radio"
                    name="advanced-fit-mode"
                    value={mode.value}
                    checked={fitMode === mode.value}
                    onChange={() => patch({ fitMode: mode.value })}
                    className="mt-0.5"
                  />
                  <span>
                    <span className="text-slate-900 dark:text-slate-100">
                      {mode.label}
                    </span>
                    <span className="ml-2 text-xs text-slate-500 dark:text-slate-400">
                      {mode.hint}
                    </span>
                  </span>
                </label>
              ))}
            </div>
          </fieldset>

          <div>
            <label className="block text-sm text-slate-700 dark:text-slate-300 mb-1">
              Applies to (questing groups)
            </label>
            <QuestingGroupChipSelector
              selectedGroupIds={groupIds}
              onChange={(next) => patch({ appliesToQuestingGroupIds: next })}
            />
            <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
              {groupIds && groupIds.length > 0
                ? `Applies only to polls in the ${groupIds.length} selected group${groupIds.length === 1 ? "" : "s"}.`
                : "Applies to all polls (global)."}
            </p>
          </div>
        </div>
      ) : null}
    </div>
  );
}

// eslint-disable-next-line react-refresh/only-export-components -- intentional: colocated test/util export, not a component
export const __test__ = { valuesDivergeFromDefaults, DEFAULT_VALUE };
